import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { Button, Field, Spinner, Switch } from "./ui/kit";
import api, { apiError } from "../lib/api";
import Vyber from "./Vyber";

// Nastavení → Workflow úkolů. U každé fáze (a u dokončení nabídky) si firma
// určí, jaké úkoly vzniknou samy: název, obchod/servis, typ, komu, termín
// „za N dnů", stálou poznámku, podmínku a kdy se úkol odškrtne sám.
// Mění jen majitel; obchodník vidí, co se kdy založí.
//
// Úpravy (přepínač, úprava, nový úkol, smazání) se nejdřív projeví jen na
// obrazovce — hned, bez čekání na server. Do systému jdou až tlačítkem Uložit
// v liště dole; Zrušit vrátí stav z posledního uložení. Kdo se překlikne,
// nic nepokazí.

const PROMENNE = "{klient}, {technologie}, {pripad}, {cislo_pripadu}, {zaloha}";

function terminText(s) {
  if (!s.termin_dni) return "hned";
  const n = s.termin_dni;
  const dny = n === 1 ? "den" : n < 5 ? "dny" : "dní";
  return `za ${n} ${s.pracovni_dny ? "prac. " : ""}${dny}`;
}

const PRAZDNA = (spoustec) => ({
  spoustec, nazev: "", oblast: "OBCHOD", typ: "Úkol", komu: "OBCHODNIK", user_id: null,
  termin_dni: 3, pracovni_dny: true, poznamky: "", podminka: "VZDY", kategorie_ids: [], uzavreni: "RUCNE", aktivni: true,
});

const POLE = ["spoustec", "nazev", "oblast", "typ", "komu", "user_id", "termin_dni", "pracovni_dny", "poznamky",
  "podminka", "kategorie_ids", "uzavreni", "aktivni"];
const telo = (s) => Object.fromEntries(POLE.map((k) => [k, k === "termin_dni" && s[k] === "" ? 0 : s[k]]));
const stejne = (a, b) => JSON.stringify(telo(a)) === JSON.stringify(telo(b));
let dalsiNove = 0;

export default function WorkflowUkoly({ muzeUpravit, onNeulozeno }) {
  const [data, setData] = useState(null);
  const [koncept, setKoncept] = useState([]);       // šablony, jak je vidí obrazovka (neuložené)
  const [ukladam, setUkladam] = useState(false);
  const [ulozeno, setUlozeno] = useState(false);
  const [tym, setTym] = useState([]);
  const [kategorie, setKategorie] = useState([]);
  const [edit, setEdit] = useState(null);          // šablona (s id = úprava, bez = nová)
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState("");

  const prijmout = (d) => { setData(d); setKoncept(d.sablony.map((s) => ({ ...s }))); };
  const load = () => api.get("/api/workflow/ukoly").then((r) => prijmout(r.data)).catch((e) => setChyba(apiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
    api.get("/api/team").then((r) => setTym((r.data.members || []).filter((m) => m.active))).catch(() => {});
    api.get("/api/meta/ciselniky").then((r) => setKategorie(r.data.kategorie || [])).catch(() => {});
  }, []);

  const skupiny = useMemo(() => {
    if (!data) return [];
    // Pořadí určuje server (fáze firmy, mezikroky za dokončenou nabídkou).
    return Object.keys(data.spoustece).map((k) => ({
      klic: k, nazev: data.spoustece[k], sablony: koncept.filter((s) => s.spoustec === k),
    }));
  }, [data, koncept]);

  // Co se liší od posledního uložení.
  const zmeny = useMemo(() => {
    if (!data) return { nove: [], upravene: [], smazane: [] };
    const puv = Object.fromEntries(data.sablony.map((s) => [s.id, s]));
    const ted = new Set(koncept.map((s) => s.id));
    return {
      nove: koncept.filter((s) => !puv[s.id]),
      upravene: koncept.filter((s) => puv[s.id] && !stejne(s, puv[s.id])),
      smazane: data.sablony.filter((s) => !ted.has(s.id)),
    };
  }, [data, koncept]);
  const pocetZmen = zmeny.nove.length + zmeny.upravene.length + zmeny.smazane.length;
  const neulozeno = pocetZmen > 0;

  useEffect(() => { onNeulozeno?.(neulozeno); }, [neulozeno, onNeulozeno]);
  useEffect(() => () => onNeulozeno?.(false), [onNeulozeno]);
  useEffect(() => {
    if (!neulozeno) return undefined;
    const varuj = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", varuj);
    return () => window.removeEventListener("beforeunload", varuj);
  }, [neulozeno]);

  const typyOblasti = (oblast) => (data?.typy || []).filter((t) => t.oblast === oblast).map((t) => t.name);

  // Úprava v okně jen přepíše řádek na obrazovce; ukládá se lištou dole.
  const pouzit = () => {
    const s = { ...edit, termin_dni: edit.termin_dni === "" ? 0 : edit.termin_dni, nazev: edit.nazev.trim() };
    if (s.id) setKoncept((k) => k.map((x) => (x.id === s.id ? s : x)));
    else setKoncept((k) => [...k, { ...s, id: `novy-${++dalsiNove}` }]);
    setEdit(null); setChyba(""); setUlozeno(false);
  };
  const prepnout = (s) => { setKoncept((k) => k.map((x) => (x.id === s.id ? { ...x, aktivni: !x.aktivni } : x))); setUlozeno(false); };
  const smazat = (s) => { setKoncept((k) => k.filter((x) => x.id !== s.id)); setUlozeno(false); };
  const zrusit = () => { setKoncept(data.sablony.map((s) => ({ ...s }))); setChyba(""); };

  const ulozit = async () => {
    setUkladam(true); setChyba("");
    let k = koncept;   // nově založeným se průběžně dosadí id ze serveru, ať se při chybě nezaloží dvakrát
    try {
      for (const s of zmeny.smazane) await api.delete(`/api/workflow/ukoly/${s.id}`);
      let preradeno = 0;
      for (const s of zmeny.upravene) {
        const { data: u } = await api.patch(`/api/workflow/ukoly/${s.id}`, telo(s));
        preradeno += u.preradeno_ukolu || 0;
      }
      setInfo(preradeno ? `Otevřené úkoly přešly na nového člověka: ${preradeno}.` : "");
      for (const s of zmeny.nove) {
        const { data: nova } = await api.post("/api/workflow/ukoly", telo(s));
        k = k.map((x) => (x === s ? { ...x, id: nova.id } : x));
      }
      const r = await api.get("/api/workflow/ukoly");
      prijmout(r.data);
      setUlozeno(true);
      setTimeout(() => setUlozeno(false), 1800);
    } catch (e) {
      // Část se mohla uložit: základ vezmi ze serveru, neuložené úpravy nech na obrazovce.
      setChyba(apiError(e.response?.data?.detail));
      try { const r = await api.get("/api/workflow/ukoly"); setData(r.data); setKoncept(k); } catch { /* zůstane, jak je */ }
    } finally { setUkladam(false); }
  };
  const vychozi = async () => {
    if (!window.confirm("Obnovit výchozí šablony? Tvoje úpravy šablon se ztratí (už založené úkoly zůstanou).")) return;
    const { data: d } = await api.post("/api/workflow/ukoly/vychozi"); prijmout(d);
  };

  if (!data) return <div className="card p-6 mt-4">{chyba ? <span className="t-meta text-danger">{chyba}</span> : <Spinner />}</div>;

  return (
    <div className="card p-5 md:p-6 mt-4" data-testid="workflow-ukoly">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="t-sekce font-700 text-ink">Workflow úkolů</h2>
          <p className="t-meta text-muted mt-0.5">
            Úkoly, které vzniknou samy, když technologie vstoupí do fáze. Každý vznikne u technologie jen jednou.
          </p>
        </div>
        {muzeUpravit && (
          <button onClick={vychozi} className="tap t-meta text-muted hover:text-ink inline-flex items-center gap-1.5" data-testid="workflow-vychozi">
            <RotateCcw size={14} /> Obnovit výchozí
          </button>
        )}
      </div>
      {chyba && !edit && !neulozeno && <div className="t-meta text-danger mb-3">{chyba}</div>}
      {info && <div className="t-meta text-success mb-3" data-testid="workflow-info">{info}</div>}

      <VychoziObchodnik data={data} tym={tym} muzeUpravit={muzeUpravit}
        onZmena={(id) => setData((d) => ({ ...d, vychozi_obchodnik_id: id }))} />

      <div className="space-y-5">
        {skupiny.map((g) => (
          <section key={g.klic} data-testid={`workflow-skupina-${g.klic}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="label-up text-muted">Když: {g.nazev}</h3>
              {muzeUpravit && (
                <button onClick={() => setEdit(PRAZDNA(g.klic))} className="tap t-meta font-600 text-accent inline-flex items-center gap-1"
                  data-testid={`workflow-pridat-${g.klic}`}><Plus size={14} /> Přidat úkol</button>
              )}
            </div>
            {g.sablony.length === 0 && <p className="t-meta text-muted-2">Žádné úkoly.</p>}
            <div className="divide-y divide-line border border-line rounded-xl">
              {g.sablony.map((s) => (
                <div key={s.id} className="px-3 py-2.5 flex items-start gap-3" data-testid={`sablona-${s.id}`}>
                  <div className={`min-w-0 flex-1 transition-opacity duration-200 ${s.aktivni ? "" : "opacity-50"}`}>
                    <div className="t-telo font-600 text-ink flex items-center gap-2">
                      <span className="min-w-0">{s.nazev}</span>
                      {(zmeny.nove.includes(s) || zmeny.upravene.includes(s)) && (
                        <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-accent" title="Neuloženo" aria-label="Neuloženo" />
                      )}
                    </div>
                    <div className="t-meta text-muted flex flex-wrap gap-x-2">
                      <span>{s.oblast === "SERVIS" ? "Servis" : "Obchod"} · {s.typ}</span>
                      <span>{s.komu === "UZIVATEL" ? (tym.find((m) => m.id === s.user_id)?.name || "člen týmu") : data.komu[s.komu]}</span>
                      <span>{terminText(s)}</span>
                      {s.podminka !== "VZDY" && <span>{data.podminky[s.podminka]}</span>}
                      {s.uzavreni !== "RUCNE" && <span>odškrtne se: {data.uzavreni[s.uzavreni].replace(/^Když /, "").toLowerCase()}</span>}
                    </div>
                  </div>
                  {muzeUpravit && (
                    <div className={`flex items-center gap-1 shrink-0 ${s.aktivni ? "" : "[&>button]:opacity-50"}`}>
                      <Switch checked={s.aktivni} onChange={() => prepnout(s)} testid={`sablona-aktivni-${s.id}`} />
                      <button onClick={() => setEdit({ ...s })} className="tap md:min-h-0 h-8 w-8 flex items-center justify-center text-muted hover:text-ink" aria-label="Upravit"><Pencil size={14} /></button>
                      <button onClick={() => smazat(s)} className="tap md:min-h-0 h-8 w-8 flex items-center justify-center text-muted hover:text-danger" aria-label="Smazat"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {muzeUpravit && (neulozeno || ulozeno) && (
        <div className="sticky bottom-0 -mx-5 md:-mx-6 -mb-5 md:-mb-6 mt-5 px-5 md:px-6 py-3 bg-white/95 backdrop-blur border-t border-line-softer rounded-b-card flex flex-wrap items-center justify-between gap-3 vjezd"
          style={{ display: "flex", paddingBottom: "max(12px, env(safe-area-inset-bottom))" }} data-testid="workflow-lista">
          <span className="t-meta text-sec flex items-center gap-2">
            {neulozeno ? (
              <><span className="h-2 w-2 rounded-full bg-accent shrink-0" />Neuložené změny: {pocetZmen}</>
            ) : "Všechno uloženo."}
          </span>
          {chyba && <span className="t-meta text-danger" data-testid="workflow-chyba">{chyba}</span>}
          <div className="flex gap-2 ml-auto">
            {neulozeno && <Button variant="ghost" className="tap md:min-h-0" onClick={zrusit} disabled={ukladam} data-testid="workflow-zrusit">Zrušit</Button>}
            <Button className="tap md:min-h-0" onClick={ulozit} disabled={!neulozeno || ukladam}
              stav={ukladam ? "nacita" : ulozeno ? "hotovo" : undefined} data-testid="workflow-ulozit">Uložit</Button>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="workflow-edit">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEdit(null)} />
          <div className="relative bg-white w-full md:max-w-[560px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
            <div className="border-b border-line-softer px-6 py-4">
              <h2 className="t-sekce font-800 text-ink">{edit.id ? "Upravit úkol" : "Nový úkol"}</h2>
              <p className="t-meta text-muted">Když: {data.spoustece[edit.spoustec]}</p>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Název úkolu" hint={`Můžeš použít ${PROMENNE}.`}>
                <input id="wf-nazev" className="field" value={edit.nazev} onChange={(e) => setEdit({ ...edit, nazev: e.target.value })} data-testid="wf-nazev" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Oblast">
                  <Vyber id="wf-oblast" className="field" value={edit.oblast} onChange={(e) => {
                    const oblast = e.target.value; setEdit({ ...edit, oblast, typ: typyOblasti(oblast)[0] || "" });
                  }}>
                    <option value="OBCHOD">Obchod</option><option value="SERVIS">Servis</option>
                  </Vyber>
                </Field>
                <Field label="Typ aktivity">
                  <Vyber id="wf-typ" className="field" value={edit.typ} onChange={(e) => setEdit({ ...edit, typ: e.target.value })}>
                    {typyOblasti(edit.oblast).map((t) => <option key={t} value={t}>{t}</option>)}
                  </Vyber>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Komu">
                  <Vyber id="wf-komu" className="field" value={edit.komu} onChange={(e) => setEdit({ ...edit, komu: e.target.value })}>
                    {Object.entries(data.komu).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Vyber>
                </Field>
                {edit.komu === "UZIVATEL" && (
                  <Field label="Člen týmu">
                    <Vyber id="wf-user" className="field" value={edit.user_id || ""} onChange={(e) => setEdit({ ...edit, user_id: e.target.value || null })}>
                      <option value="">Vyber…</option>
                      {tym.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </Vyber>
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <Field label="Termín: za kolik dnů">
                  <input id="wf-dni" type="number" min="0" max="365" className="field amount" value={edit.termin_dni}
                    onChange={(e) => setEdit({ ...edit, termin_dni: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) })} />
                </Field>
                <label className="flex items-center gap-2 t-telo text-sec pb-2.5">
                  <input type="checkbox" checked={edit.pracovni_dny} onChange={(e) => setEdit({ ...edit, pracovni_dny: e.target.checked })} />
                  pracovních dnů
                </label>
              </div>
              <Field label="Poznámka (vždy stejná)">
                <textarea id="wf-poznamky" className="field min-h-[70px] py-2" value={edit.poznamky} onChange={(e) => setEdit({ ...edit, poznamky: e.target.value })} />
              </Field>
              <Field label="Podmínka">
                <Vyber id="wf-podminka" className="field" value={edit.podminka} onChange={(e) => setEdit({ ...edit, podminka: e.target.value })}>
                  {Object.entries(data.podminky).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Vyber>
              </Field>
              {edit.podminka === "TECHNOLOGIE" && (
                <div className="flex flex-wrap gap-2">
                  {kategorie.map((k) => {
                    const on = edit.kategorie_ids.includes(k.id);
                    return (
                      <button key={k.id} type="button" onClick={() => setEdit({ ...edit, kategorie_ids: on ? edit.kategorie_ids.filter((x) => x !== k.id) : [...edit.kategorie_ids, k.id] })}
                        className={`tap md:min-h-0 pill px-3 h-8 t-meta font-600 border ${on ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong"}`}>{k.name}</button>
                    );
                  })}
                </div>
              )}
              <Field label="Odškrtne se sám">
                <Vyber id="wf-uzavreni" className="field" value={edit.uzavreni} onChange={(e) => setEdit({ ...edit, uzavreni: e.target.value })}>
                  {Object.entries(data.uzavreni).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Vyber>
              </Field>
              {chyba && <div className="t-meta text-danger">{chyba}</div>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setEdit(null); setChyba(""); }}>Zrušit</Button>
                <Button onClick={pouzit} disabled={!edit.nazev.trim()} data-testid="wf-ulozit">Použít</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Komu patří nové případy — a s nimi úkoly „Obchodník případu“. Ukládá se hned.
function VychoziObchodnik({ data, tym, muzeUpravit, onZmena }) {
  const [chyba, setChyba] = useState("");
  const lide = tym.filter((m) => m.role !== "SUBDODAVATEL");
  const hodnota = data.vychozi_obchodnik_id || "";
  const zmenit = async (e) => {
    const id = e.target.value || null;
    setChyba("");
    try { await api.put("/api/workflow/vychozi-obchodnik", { user_id: id }); onZmena(id); }
    catch (err) { setChyba(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="mb-5 rounded-xl border border-line px-3 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4"
      data-testid="workflow-vychozi-obchodnik">
      <div className="min-w-0 flex-1">
        <div className="t-telo font-600 text-ink">Obchodník nových případů</div>
        <div className="t-meta text-muted">Dostane úkoly „Obchodník případu“. U případu jde změnit — otevřené úkoly přejdou s ním.</div>
        {chyba && <div className="t-meta text-danger mt-1">{chyba}</div>}
      </div>
      {muzeUpravit ? (
        <select className="field w-full md:w-60 min-w-0" value={hodnota} onChange={zmenit}
          aria-label="Obchodník nových případů" data-testid="workflow-vychozi-obchodnik-vyber">
          <option value="">Kdo případ zakládá</option>
          {hodnota && !lide.some((m) => m.id === hodnota) && <option value={hodnota}>…</option>}
          {lide.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      ) : (
        <span className="t-telo text-ink">{lide.find((m) => m.id === hodnota)?.name || "Kdo případ zakládá"}</span>
      )}
    </div>
  );
}
